import React, { ReactElement } from 'react';
import { i18n } from 'config/locales';
import { countryValueResolver } from 'helpers/location';
import {
  ApplicableModel, CheckboxModel, LocationModel, YesNoIDKModel
} from 'forms/shared/types';
import { CommonAddressBooks, CommonOnError } from 'types/props';
import {
  createLocationModelObjectFromProps,
  getCountryValue,
  isSafeInternational,
  isSafeUnitedStates,
} from 'helpers/locations';
import { COUNTRY_FOREIGN } from 'constants/enums/locations';
import LocationValidator, { countryString } from '../../../validators/location';
import ValidationElement from '../ValidationElement';
import Street from '../Street';
import State from '../State';
import City from '../City';
import Country from '../Country';
import ZipCode from '../ZipCode';
import RadioGroup from '../RadioGroup';
import Radio from '../Radio';
import Suggestions from '../Suggestions';
import { AddressSuggestion } from './AddressSuggestion';
import { setAttribute } from '../shared/setAttribute';
import { ValidationElementProps } from '../ValidationElement/ValidationElement';
import SvgComponent from '../SvgComponent';
import MilitaryInstallation from '../MilitaryInstallation';
import Checkbox from '../Checkbox';
import NotApplicable from '../NotApplicable';
import Show from '../Show';

interface Props extends ValidationElementProps, Partial<LocationModel> {
  addressUnknown?: ApplicableModel
  addressUnknownLabel?: string
  inUsaUnknown?: ApplicableModel
  inUsaUnknownLabel?: string
  hideInstallationNameIfInternational?: boolean
  hideInstallationNameUnknownExplanation?: boolean
  name?: string
  onUpdate?: (address: Partial<LocationModel>, delay?: number | null) => void
  onUpdateAddressUnknown?: (addressUnknown: ApplicableModel) => void
  onUpdateInUsaUnknown?: (inUsaUnknown: ApplicableModel) => void
  showAddressUnknown?: boolean
  showApoFpoUnknown?: boolean
  showCityUnknown?: boolean
  showCountryNotApplicable?: boolean,
  showInUsaUnknown?: boolean
  showInstallationNameUnknown?: boolean
  showIsInstallationUnknownIfDomestic?: boolean
  showIsInstallationUnknownIfInternational?: boolean
  showStateUnknown?: boolean
  showStreetUnknown?: boolean
  showZipcodeUnknown?: boolean
  onError?: CommonOnError
  onFocus?: any
  isEnabled?: boolean
  addressBook?: string
  addressBooks?: CommonAddressBooks
  disableToggle?: boolean
  label?: string
  disabled?: boolean
  attribute?: string
  streetLabel?: string
  cityLabel?: string
  stateLabel?: string
  zipcodeLabel?: string
  countryLabel?: string
  domesticTitle?: string
  foreignTitle?: string
  includeMilitaryStates?: boolean
  isMilitaryInstallation?: YesNoIDKModel
  showMilitaryInstallation?: boolean
  streetPlaceholder?: string
  required?: boolean
  showInternationalStreet?: boolean
  showInternationalStreetUnknown?: boolean
  internationalStreetLabel?: string
  isPoBoxAllowed?: boolean
  useChangeWarning: boolean
  confirmationWindowText: string
  apoFpoDpoPostcode?: string,
  isMilitaryDiplomaticAddress?: YesNoIDKModel,
  militaryDiplomaticInstallationName?: string,
  militaryInstallationNameNotApplicable?: ApplicableModel,
  militaryDiplomaticInstallationNameExplanation?: string,
}

interface AddressState {
  uid: string
  showAddressBook: boolean
}

export default class AddressUnknown extends ValidationElement<Props, AddressState> {
  constructor(props: Props) {
    super(props);

    this.state = {
      uid: `${this.props.name}-${super.guid()}`,
      showAddressBook: false,
    };

    this.blurred = {
      street: true,
      city: true,
      state: true,
      country: true,
      zipcode: true,
    };

    this.apoZipcode = React.createRef();

    this.errors = [];
  }

  onAddressUpdate = (nextValue) => {
    const { name, value } = nextValue;

    this.update({
      [name]: value,
    });
  };

  updateCountry = (values) => {
    this.update({
      country: { value: values.value ? values.value : '' },
      countryComments: values.comments,
    });
  };

  updateAddressType = (values: Partial<LocationModel>) => {
    // Check if data is entered in fields being cleared, prompt the user
    // for confirmation if so before deleting any data
    if (this.props.useChangeWarning === true) {
      if (this.props.city
        || this.props.cityUnknown?.applicable === false
        || this.props.state
        || this.props.stateUnknown?.applicable === false
        || this.props.street
        || this.props.streetUnknown?.applicable === false
        || this.props.zipcode
        || this.props.zipcodeUnknown?.applicable === false
        || this.props.apoFpoDpoPostcode
        || this.props.isMilitaryDiplomaticAddress
        || this.props.militaryDiplomaticInstallationName
        || this.props.militaryInstallationNameNotApplicable
        || this.props.militaryDiplomaticInstallationNameExplanation
        || (
          this.props.country.value
          && this.props.country.value !== 'United States'
          && this.props.country.value !== 'INTERNATIONAL'
        )
      ) {
        // eslint-disable-next-line no-alert
        if (window?.confirm(this.props.confirmationWindowText) === false) {
          return;
        }
      }
    }

    // Set existing errors to null when toggling fields
    this.props.onError(
      values.value,
      this.errors.map((err) => ({
        code: err.code,
        valid: null,
        uid: err.uid,
      }))
    );

    const country = { value: '' };

    // POSTOFFICE is used for APO, FPO and DPO (DPO addresses are not permitted by eQip)
    if (!isSafeInternational(values.value)) {
      country.value = values.value;
      this.blurred = {
        street: true,
        city: true,
        state: true,
        country: true,
        zipcode: true,
      };
    } else {
      country.value = COUNTRY_FOREIGN;
    }

    this.update({
      country,
      city: '',
      cityUnknown: { applicable: true },
      state: '',
      stateUnknown: { applicable: true },
      zipcode: '',
      zipcodeUnknown: { applicable: true },
      street: '',
      streetUnknown: { applicable: true },
      apoFpoDpoPostcode: '',
      isMilitaryDiplomaticAddress: '',
      militaryDiplomaticInstallationName: '',
      militaryInstallationNameNotApplicable: '',
      militaryDiplomaticInstallationNameExplanation: '',
    });
  };

  update = (values: Partial<LocationModel>, delay: number | null = null, blur = false) => {
    // Get the next values
    const next: Partial<LocationModel> = {
      street: this.props.street,
      streetUnknown: this.props.streetUnknown,
      city: this.props.city,
      cityUnknown: this.props.cityUnknown,
      state: this.props.state,
      stateUnknown: this.props.stateUnknown,
      country: this.props.country,
      countryNotApplicable: this.props.countryNotApplicable,
      zipcode: this.props.zipcode,
      zipcodeUnknown: this.props.zipcodeUnknown,
      validated: this.props.validated,
      apoFpoDpoPostcode: this.props.apoFpoDpoPostcode,
      apoFpoNotApplicable: this.props.apoFpoNotApplicable,
      isMilitaryDiplomaticAddress: this.props.isMilitaryDiplomaticAddress,
      militaryDiplomaticInstallationName: this.props.militaryDiplomaticInstallationName,
      militaryInstallationNameNotApplicable: this.props.militaryInstallationNameNotApplicable,
      militaryDiplomaticInstallationNameExplanation: this.props.militaryDiplomaticInstallationNameExplanation,
      ...values,
    };

    // Determine if previous values were the same
    const same = next.street === this.props.street
      && next.streetUnknown?.applicable === this.props.streetUnknown?.applicable
      && next.city === this.props.city
      && next.cityUnknown?.applicable === this.props.cityUnknown?.applicable
      && next.state === this.props.state
      && next.stateUnknown?.applicable === this.props.stateUnknown?.applicable
      && getCountryValue(next.country) === getCountryValue(this.props.country)
      && next.countryNotApplicable?.applicable === this.props.countryNotApplicable?.applicable
      && next.zipcode === this.props.zipcode
      && next.zipcodeUnknown === this.props.zipcodeUnknown
      && next.apoFpoDpoPostcode === this.props.apoFpoDpoPostcode
      && next.apoFpoNotApplicable === this.props.apoFpoNotApplicable
      && next.isMilitaryDiplomaticAddress === this.props.isMilitaryDiplomaticAddress
      && next.militaryDiplomaticInstallationName === this.props.militaryDiplomaticInstallationName
      && next.militaryInstallationNameNotApplicable === this.props.militaryInstallationNameNotApplicable
      && next.militaryDiplomaticInstallationNameExplanation === this.props.militaryDiplomaticInstallationNameExplanation;

    // If it is not the same then we need to force validation
    next.validated = same && this.props.validated;
    // Update the properties
    if (!same || blur) {
      this.props.onUpdate?.(next, delay);
    }
  };

  blurForceUpdate = (values: Partial<LocationModel>) => {
    const blurry = Object.values(this.blurred).every((value) => !!value);
    const modal = document.querySelector('.modal');
    let changed = false;
    for (const name in values) {
      if (this.props?.[name] !== values[name]) {
        changed = true;
      }
    }

    if (blurry && !modal && (!this.props.validated || changed)) {
      this.update(values, 500, true);
    }
  };

  updateAddressUnknown = (addressUnknownCheckbox: CheckboxModel) => {
    const addressUnknown: ApplicableModel = {
      ...this.props.addressUnknown,
      applicable: !addressUnknownCheckbox.checked,
    };

    this.props.onUpdateAddressUnknown?.(addressUnknown);
  };

  updateCityUnknown = (cityUnknown: ApplicableModel) => {
    const queue: Partial<LocationModel> = { cityUnknown };

    if (cityUnknown.applicable === false) {
      queue.city = '';
    }

    this.update(queue);
  };

  updateCountryNotApplicable = (countryNotApplicable: ApplicableModel) => {
    const queue: Partial<LocationModel> = { countryNotApplicable };

    if (countryNotApplicable.applicable === false) {
      queue.country = { value: COUNTRY_FOREIGN };
    }

    this.update(queue);
  };

  updateInUsaUnknown = (inUsaUnknownCheckbox: CheckboxModel) => {
    const inUsaUnknown: ApplicableModel = {
      ...this.props.inUsaUnknown,
      applicable: !inUsaUnknownCheckbox.checked,
    };

    this.props.onUpdateInUsaUnknown?.(inUsaUnknown);
  };

  updateInternationalField = ({ name, value }) => {
    if (name) {
      const values = { [name]: value };
      this.props.onUpdate({
        street: this.props.street,
        city: this.props.city,
        country: this.props.country,
        ...values
      });
    }
  };

  updateMilitaryInstallation = (values: Partial<LocationModel>) => {
    this.update({
      apoFpoDpoPostcode: this.props.apoFpoDpoPostcode,
      apoFpoNotApplicable: this.props.apoFpoNotApplicable,
      isMilitaryDiplomaticAddress: this.props.isMilitaryDiplomaticAddress,
      militaryDiplomaticInstallationName: this.props.militaryDiplomaticInstallationName,
      militaryInstallationNameNotApplicable: this.props.militaryInstallationNameNotApplicable,
      militaryDiplomaticInstallationNameExplanation: this.props.militaryDiplomaticInstallationNameExplanation,
      ...values,
    });
  };

  updateStateUnknown = (stateUnknown: ApplicableModel) => {
    const queue: Partial<LocationModel> = { stateUnknown };

    if (stateUnknown.applicable === false) {
      queue.state = '';
    }

    this.update(queue);
  };

  updateStreetUnknown = (streetUnknown: ApplicableModel) => {
    const queue: Partial<LocationModel> = { streetUnknown };

    if (streetUnknown.applicable === false) {
      queue.street = '';
    }

    this.update(queue);
  };

  updateZipcodeUnknown = (zipcodeUnknown: ApplicableModel) => {
    const queue: Partial<LocationModel> = { zipcodeUnknown };

    if (zipcodeUnknown.applicable === false) {
      queue.zipcode = '';
    }

    this.update(queue);
  };

  focusField = (event) => {
    const field = event.target.name;

    this.props.onFocus(event);
    this.blurred[field] = false;
  };

  blurField = (event) => {
    const { name, value } = event.target;

    this.blurred[name] = true;
    this.blurForceUpdate({ [name]: value });
    super.handleBlur(event);
  };

  addressType = () => {
    // If address is not applicable ("I don't know"), return ""
    if (!this.props.isEnabled || !this.props.inUsaUnknown?.applicable) {
      return '';
    }
    if (JSON.stringify(this.props?.country) === '{}') {
      return null;
    }
    const locationObject = createLocationModelObjectFromProps(this.props);

    if (isSafeUnitedStates(locationObject, false)) { return 'United States'; }
    if (isSafeInternational(locationObject, false)) { return 'International'; }

    // default to US if no country provided
    return 'United States';
  };

  openAddressBook = () => this.setState({ showAddressBook: true });

  closeAddressBook = () => this.setState({ showAddressBook: false });

  renderAddressBookItem = (suggestion) => (
    <AddressSuggestion suggestion={suggestion} current={suggestion} />
  );

  selectAddressBookItem = (suggestion) => {
    this.setState({ showAddressBook: false }, () => {
      this.update({
        street: suggestion.street,
        city: suggestion.city,
        state: suggestion.state,
        zipcode: suggestion.zipcode,
        county: suggestion.county,
        country: suggestion.country,
        validated: true,
      });
    });
  };

  handleError = (value, arr) => {
    // eslint-disable-next-line no-param-reassign
    arr = arr.map((err) => ({
      code: `address.${err.code}`,
      valid: err.valid,
      uid: err.uid,
    }));

    const requiredErr = arr.concat(
      this.constructor.errors.map((err) => ({
        code: `address.${err.code}`,
        valid: err.func(value, { ...this.props }),
        uid: this.state.uid,
      }))
    );

    this.storeErrors(requiredErr);
    this.props.onError(value, requiredErr);
    return arr;
  };

  storeErrors = (errors) => {
    const newErrors = [...errors];

    for (let i = 0; i < newErrors.length; i += 1) {
      const e = newErrors[i];
      const idx = this.errors.findIndex((x) => x.uid === e.uid && x.code === e.code);
      if (idx !== -1) {
        this.errors[idx] = { ...e };
      } else {
        this.errors.push({ ...e });
      }
    }
  };

  filteredAddressBook(book) {
    const {
      city, country, state, street, zipcode
    } = this.props;
    return book.filter(
      (address) => address.city !== city
        || address.country !== country
        || address.state !== state
        || address.street !== street
        || address.zipcode !== zipcode
    );
  }

  render() {
    const book = this.filteredAddressBook(this.props.addressBooks[this.props.addressBook] || []);
    const locationValidator = new LocationValidator(this.props);
    const instateZipcode: boolean = locationValidator.validZipcodeState();
    const locationObject = createLocationModelObjectFromProps(this.props);
    let title = i18n.t('address.pvq.domesticTitle');
    if (isSafeUnitedStates(locationObject, false)) {
      if (this.props.domesticTitle) {
        title = this.props.domesticTitle;
      }
    } else if (this.props.foreignTitle) {
      title = this.props.foreignTitle;
    } else {
      title = i18n.t('address.pvq.foreignTitle');
    }

    const isInternational: boolean = isSafeInternational(locationObject, false);

    const addressIsUnknown = !this.props.addressUnknown?.applicable;
    const inUsaUnknown = !this.props.inUsaUnknown?.applicable;

    return (
      <div className="address">
        {!this.props.disableToggle && (
          <>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <h4>{this.props.label}</h4>
            <RadioGroup
              className={`address-options option-list no-postoffice`.trim()}
              disabled={this.props.disabled || inUsaUnknown}
              selectedValue={this.addressType()}
            >
              <Radio
                name="addressType"
                label="Yes"
                value="United States"
                className="domestic"
                ignoreDeselect
                disabled={this.props.disabled}
                onUpdate={this.updateAddressType}
                onFocus={this.props.onFocus}
              />
              <Radio
                name="addressType"
                label="No"
                value="International"
                className="international"
                ignoreDeselect
                disabled={this.props.disabled}
                onUpdate={this.updateAddressType}
                onFocus={this.props.onFocus}
              />
            </RadioGroup>
          </>
        )}

        {this.props.showInUsaUnknown === true && (
          <Checkbox
            checked={inUsaUnknown}
            label={this.props.inUsaUnknownLabel}
            name="InUsaUnknown"
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            onError={this.props.onError}
            onUpdate={this.updateInUsaUnknown}
          />
        )}
        <Show when={this.addressType()}>
          {book.length > 0 && (
            <div className="reuse-address">
              <button
                type="button"
                className="reuse-address-open-modal"
                title={i18n.t('address.addressBook.reuse')}
                onClick={this.openAddressBook}
              >
                <div className="address-book" aria-hidden="true">
                  <SvgComponent className="address-book" src="/img/address-book.svg" />
                </div>
                <span>{i18n.t('address.addressBook.reuse')}</span>
              </button>
            </div>
          )}

          <Suggestions
            show={this.state.showAddressBook}
            suggestions={book}
            renderSuggestion={this.renderAddressBookItem}
            suggestionTitle={i18n.t('suggestions.addressBook.title')}
            suggestionParagraph={book.length > 0 ? i18n.m('suggestions.addressBook.para') : ''}
            suggestionLabel={i18n.t('suggestions.addressBook.label')}
            suggestionUseLabel={i18n.t('suggestions.addressBook.use')}
            suggestionDismissLabel={i18n.t('suggestions.addressBook.dismiss')}
            onSuggestion={this.selectAddressBookItem}
            onDismiss={this.closeAddressBook}
          />

          <h4 className="fields-title">{title}</h4>
          {this.props.showAddressUnknown === true && (
            <Checkbox
              checked={addressIsUnknown}
              label={this.props.addressUnknownLabel}
              name="AddressUnknown"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateAddressUnknown}
              disabled={inUsaUnknown}
            />
          )}

          <div className="fields">
            {this.props.isEnabled && isSafeUnitedStates(locationObject, false) && (
              <div>
                <div className="usa-form-control">
                  {this.props.showStreetUnknown !== true && this.renderStreetForUnitedStates(addressIsUnknown, inUsaUnknown)}
                  {this.props.showStreetUnknown === true && (
                    <NotApplicable
                      applicable={this.props.streetUnknown?.applicable}
                      attribute={setAttribute(this.props.attribute, 'streetUnknown')}
                      disabled={this.props.disabled || addressIsUnknown || inUsaUnknown}
                      label={i18n.t('address.pvq.idkCapitalized')}
                      name="streetUnknown"
                      or={i18n.t('address.pvq.para.or')}
                      onUpdate={this.updateStreetUnknown}
                      useChangeWarning
                    >
                      {this.renderStreetForUnitedStates(addressIsUnknown, inUsaUnknown)}
                    </NotApplicable>
                  )}
                </div>
                <div className="usa-form-control">
                  {this.props.showCityUnknown !== true && this.renderCityForUnitedStates(addressIsUnknown, inUsaUnknown)}
                  {this.props.showCityUnknown === true && (
                    <NotApplicable
                      applicable={this.props.cityUnknown?.applicable}
                      attribute={setAttribute(this.props.attribute, 'cityUnknown')}
                      disabled={this.props.disabled || addressIsUnknown || inUsaUnknown}
                      label={i18n.t('address.pvq.idkCapitalized')}
                      name="cityUnknown"
                      or={i18n.t('address.pvq.para.or')}
                      onUpdate={this.updateCityUnknown}
                      useChangeWarning
                    >
                      {this.renderCityForUnitedStates(addressIsUnknown, inUsaUnknown)}
                    </NotApplicable>
                  )}
                </div>
                <div className="state-zip-wrap">
                  {this.props.showStateUnknown !== true && this.renderStateForUnitedStates(addressIsUnknown, inUsaUnknown)}
                  {this.props.showStateUnknown === true && (
                    <NotApplicable
                      applicable={this.props.stateUnknown?.applicable}
                      attribute={setAttribute(this.props.attribute, 'stateUnknown')}
                      disabled={this.props.disabled || addressIsUnknown || inUsaUnknown}
                      label={i18n.t('address.pvq.idkCapitalized')}
                      name="stateUnknown"
                      or={i18n.t('address.pvq.para.or')}
                      onUpdate={this.updateStateUnknown}
                      useChangeWarning
                    >
                      {this.renderStateForUnitedStates(addressIsUnknown, inUsaUnknown)}
                    </NotApplicable>
                  )}
                  {this.props.showZipcodeUnknown !== true && this.renderZipcodeForUnitedStates(addressIsUnknown, inUsaUnknown, instateZipcode)}
                  {this.props.showZipcodeUnknown === true && (
                    <NotApplicable
                      applicable={this.props.zipcodeUnknown?.applicable}
                      attribute={setAttribute(this.props.attribute, 'zipcodeUnknown')}
                      disabled={this.props.disabled || addressIsUnknown || inUsaUnknown}
                      label={i18n.t('address.pvq.idkCapitalized')}
                      name="zipcodeUnknown"
                      or={i18n.t('address.pvq.para.or')}
                      onUpdate={this.updateZipcodeUnknown}
                      useChangeWarning
                    >
                      {this.renderZipcodeForUnitedStates(addressIsUnknown, inUsaUnknown, instateZipcode)}
                    </NotApplicable>
                  )}
                </div>
              </div>
            )}

            {this.props.isEnabled && isInternational && (
              <>
                {this.props.showInternationalStreet && !this.props.showInternationalStreetUnknown && (this.renderStreetForInternational(addressIsUnknown, inUsaUnknown))}
                {this.props.showInternationalStreet && this.props.showInternationalStreetUnknown && (
                  <NotApplicable
                    applicable={this.props.streetUnknown?.applicable}
                    attribute={setAttribute(this.props.attribute, 'streetUnknown')}
                    disabled={this.props.disabled || addressIsUnknown || inUsaUnknown}
                    label={i18n.t('address.pvq.idkCapitalized')}
                    name="streetUnknown"
                    or={i18n.t('address.pvq.para.or')}
                    onUpdate={this.updateStreetUnknown}
                    useChangeWarning
                  >
                    {this.renderStreetForInternational(addressIsUnknown, inUsaUnknown)}
                  </NotApplicable>
                )}
                <div className="usa-form-control">
                  {this.props.showCityUnknown !== true && this.renderCityForInternational(addressIsUnknown, inUsaUnknown)}
                  {this.props.showCityUnknown === true && (
                    <NotApplicable
                      applicable={this.props.cityUnknown?.applicable}
                      attribute={setAttribute(this.props.attribute, 'cityUnknown')}
                      disabled={this.props.disabled || addressIsUnknown || inUsaUnknown}
                      label={i18n.t('address.pvq.idkCapitalized')}
                      name="cityUnknown"
                      or={i18n.t('address.pvq.para.or')}
                      onUpdate={this.updateCityUnknown}
                      useChangeWarning
                    >
                      {this.renderCityForInternational(addressIsUnknown, inUsaUnknown)}
                    </NotApplicable>
                  )}
                </div>
                <div className="usa-form-control">
                  {this.props.showCountryNotApplicable !== true && this.renderCountry(addressIsUnknown, inUsaUnknown)}
                  {this.props.showCountryNotApplicable === true && (
                    <NotApplicable
                      applicable={this.props.countryNotApplicable?.applicable}
                      attribute={setAttribute(this.props.attribute, 'countryNotApplicable')}
                      disabled={this.props.disabled || addressIsUnknown || inUsaUnknown}
                      label={i18n.t('address.pvq.idkCapitalized')}
                      name="countryNotApplicable"
                      or={i18n.t('address.pvq.para.or')}
                      onUpdate={this.updateCountryNotApplicable}
                      useChangeWarning
                    >
                      {this.renderCountry(addressIsUnknown, inUsaUnknown)}
                    </NotApplicable>
                  )}
                </div>
              </>
            )}
            {this.props.isEnabled && this.props.showMilitaryInstallation && (
              <div>
                <MilitaryInstallation
                  attribute={this.props.attribute}
                  branchDnkLabel={i18n.t('address.pvq.idkCapitalized')}
                  branchLabel={isInternational ? i18n.t('address.pvq.installation.foreignBranch.label') : i18n.t('address.pvq.installation.branch.label')}
                  hideInstallationName={isInternational && this.props.hideInstallationNameIfInternational}
                  nameTitle={isInternational ? i18n.t('address.pvq.installation.foreign.title') : i18n.t('address.pvq.installation.name.title')}
                  disabled={this.props.disabled || addressIsUnknown || inUsaUnknown}
                  hideInstallationNameUnknownExplanation={this.props.hideInstallationNameUnknownExplanation}
                  isForeignInstallation={isInternational}
                  apoFpoDpoPostcode={this.props.apoFpoDpoPostcode}
                  apoFpoNotApplicable={this.props.apoFpoNotApplicable}
                  isMilitaryDiplomaticAddress={this.props.isMilitaryDiplomaticAddress}
                  militaryDiplomaticInstallationName={this.props.militaryDiplomaticInstallationName}
                  militaryInstallationNameNotApplicable={this.props.militaryInstallationNameNotApplicable}
                  militaryDiplomaticInstallationNameExplanation={this.props.militaryDiplomaticInstallationNameExplanation}
                  onUpdate={this.updateMilitaryInstallation}
                  onFocus={this.props.onFocus}
                  onError={this.handleError}
                  showIsInstallationUnknown={(isInternational && this.props.showIsInstallationUnknownIfInternational) || (isSafeUnitedStates(locationObject, false) && this.props.showIsInstallationUnknownIfDomestic)}
                  showInstallationNameUnknown={this.props.showInstallationNameUnknown}
                  hideInstallationNameUnknownWhenForeign={this.props.hideInstallationNameUnknownWhenForeign}
                  showApoFpoUnknown={this.props.showApoFpoUnknown}
                  showApoFpoDpoPostcodeWhenForeign={this.props.showApoFpoDpoPostcodeWhenForeign}
                />
              </div>
            )}
          </div>
        </Show>
      </div>
    );
  }

  private renderCityForUnitedStates(
    addressIsUnknown: boolean,
    inUsaUnknown: boolean,
  ): ReactElement {
    return (
      <City
        attribute={setAttribute(this.props.attribute, 'city')}
        className="city required"
        disabled={this.props.disabled || addressIsUnknown || inUsaUnknown}
        label={this.props.cityLabel}
        name="city"
        onBlur={this.blurField}
        onError={this.handleError}
        onFocus={this.focusField}
        onUpdate={this.onAddressUpdate}
        required={this.props.required}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        showDisabledText={this.props.showDisabledText}
        value={this.props.city}
      />
    );
  }

  private renderStateForUnitedStates(
    addressIsUnknown: boolean,
    inUsaUnknown: boolean,
  ): ReactElement {
    return (
      <State
        attribute={setAttribute(this.props.attribute, 'state')}
        className="state required"
        disabled={this.props.disabled || addressIsUnknown || inUsaUnknown}
        includeMilitaryStates={this.props.includeMilitaryStates}
        includeStates="true"
        label={this.props.stateLabel}
        name="state"
        onBlur={this.blurField}
        onError={this.handleError}
        onFocus={this.focusField}
        onUpdate={this.onAddressUpdate}
        required={this.props.required}
        value={this.props.state}
      />
    );
  }

  private renderStreetForUnitedStates(
    addressIsUnknown: boolean,
    inUsaUnknown: boolean,
  ): ReactElement {
    return (
      <Street
        attribute={setAttribute(this.props.attribute, 'street')}
        className="mailing street required"
        disabled={this.props.disabled || addressIsUnknown || inUsaUnknown}
        isPoBoxAllowed={this.props.isPoBoxAllowed}
        label={this.props.streetLabel}
        name="street"
        onBlur={this.blurField}
        onError={this.handleError}
        onFocus={this.focusField}
        onUpdate={this.onAddressUpdate}
        placeholder={this.props.streetPlaceholder}
        required={this.props.required}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        showDisabledText={this.props.showDisabledText}
        value={this.props.street}
      />
    );
  }

  private renderZipcodeForUnitedStates(
    addressIsUnknown: boolean,
    inUsaUnknown: boolean,
    instateZipcode: boolean,
  ): ReactElement {
    return (
      <ZipCode
        attribute={setAttribute(this.props.attribute, 'zipcode')}
        name="zipcode"
        key="us_zipcode"
        className="zipcode required"
        label={this.props.zipcodeLabel}
        value={this.props.zipcode}
        status={instateZipcode}
        onUpdate={this.onAddressUpdate}
        onError={this.handleError}
        onFocus={this.focusField}
        onBlur={this.blurField}
        required={this.props.required}
        disabled={this.props.disabled || addressIsUnknown || inUsaUnknown}
        showDisabledText={this.props.showDisabledText}
      />
    );
  }

  private renderStreetForInternational(
    addressIsUnknown: boolean,
    inUsaUnknown: boolean,
  ): ReactElement {
    return (
      <div className="usa-form-control">
        <Street
          attribute={setAttribute(this.props.attribute, 'street')}
          name="street"
          label={this.props.internationalStreetLabel}
          placeholder={this.props.streetPlaceholder}
          className="mailing street required"
          value={this.props.street}
          onUpdate={this.updateInternationalField}
          onError={this.handleError}
          onFocus={this.props.onFocus}
          required={this.props.required}
          disabled={this.props.disabled || addressIsUnknown || inUsaUnknown}
          isPoBoxAllowed={this.props.isPoBoxAllowed}
          showDisabledText={this.props.showDisabledText}
        />
      </div>
    )
  }

  private renderCityForInternational(
    addressIsUnknown: boolean,
    inUsaUnknown: boolean,
  ): ReactElement {
    return (
      <City
        attribute={setAttribute(this.props.attribute, 'city')}
        className="city required"
        disabled={this.props.disabled || addressIsUnknown || inUsaUnknown}
        label={this.props.cityLabel}
        name="city"
        onBlur={this.blurField}
        onError={this.handleError}
        onFocus={this.focusField}
        onUpdate={this.updateInternationalField}
        required={this.props.required}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        showDisabledText={this.props.showDisabledText}
        value={this.props.city}
      />
    );
  }

  private renderCountry(
    addressIsUnknown: boolean,
    inUsaUnknown: boolean,
  ): ReactElement {
    return (
      <Country
        isPartOfLocationModel
        attribute={setAttribute(this.props.attribute, 'country')}
        name="country"
        className="required"
        label={this.props.countryLabel}
        {...countryValueResolver(this.props)}
        excludeUnitedStates="true"
        onUpdate={this.updateCountry}
        onError={this.handleError}
        onFocus={this.props.onFocus}
        required={this.props.required}
        disabled={this.props.disabled || addressIsUnknown || inUsaUnknown}
        updateCountryFocused={this.props.updateCountryFocused}
      />
    );
  }
}

AddressUnknown.defaultProps = {
  addressUnknown: { applicable: true },
  addressUnknownLabel: i18n.t('address.pvq.idkCapitalized'),
  cityUnknown: { applicable: true },
  countryNotApplicable: { applicable: true },
  inUsaUnknown: { applicable: true },
  inUsaUnknownLabel: i18n.t('address.pvq.idkCapitalized'),
  hideInstallationNameIfInternational: false,
  hideInstallationNameUnknownExplanation: false,
  label: i18n.t('address.pvq.label'),
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onUpdateAddressUnknown: (addressUnknown: ApplicableModel) => { },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onUpdateInUsaUnknown: (inUsaUnknown: ApplicableModel) => { },
  showAddressUnknown: false,
  showApoFpoDpoPostcodeWhenForeign: false,
  showApoFpoUnknown: false,
  showCityUnknown: false,
  showCountryNotApplicable: false,
  showInUsaUnknown: false,
  showInstallationNameUnknown: false,
  hideInstallationNameUnknownWhenForeign: false,
  showIsInstallationUnknownIfDomestic: false,
  showIsInstallationUnknownIfInternational: false,
  showStateUnknown: false,
  showStreetUnknown: false,
  showZipcodeUnknown: false,
  stateUnknown: { applicable: true },
  streetUnknown: { applicable: true },
  tab: (input) => {
    input.focus();
  },
  zipcodeUnknown: { applicable: true },
  country: {},
  onFocus: () => { },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onUpdate: (address: Partial<LocationModel>, delay?: number | null) => { },
  onError: (value, arr) => arr,
  isEnabled: true,
  streetLabel: i18n.t('address.pvq.us.street.label'),
  internationalStreetLabel: i18n.t('address.pvq.us.street.label'),
  showMilitaryInstallation: false,
  confirmationWindowText: i18n.t('address.confirmation'),
  addressBooks: {},
  addressBook: '',
  isPoBoxAllowed: true,
  includeMilitaryStates: false,
  useChangeWarning: false,
  showDisabledText: false,
  apoFpoDpoPostcode: '',
  apoFpoNotApplicable: { applicable: true },
  isMilitaryDiplomaticAddress: '',
  militaryDiplomaticInstallationName: '',
  militaryInstallationNameNotApplicable: { applicable: true },
  militaryDiplomaticInstallationNameExplanation: '',
  showInternationalStreet: false,
  showInternationalStreetUnknown: false,
};

AddressUnknown.errors = [
  {
    code: 'required',
    func: (value, props) => {
      if (props.required) {
        const country = countryString(props.country);
        switch (country) {
          case 'United States':
            return !!props.street && !!props.city && !!props.state && !!props.zipcode;
          default:
            return !!props.street && !!props.city && !!country;
        }
      }
      return true;
    },
  },
];
